import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useBranding } from "../hooks/useBranding";
import investmentService from "../services/investmentService";
import {
  TrendingUp, Shield, Zap, Users, BarChart2, Globe,
  ArrowRight, CheckCircle, Star, ChevronRight, Lock,
  Clock, Activity, Cpu, Award, Quote
} from "lucide-react";

/* ─── Testimonials data (static – multilingual via i18n) ─────────────── */
const TESTIMONIALS = [
  {
    nameKey: "testimonials.user1_name",
    roleKey: "testimonials.user1_role",
    contentKey: "testimonials.user1_content",
    rating: 5,
    avatar: "AM",
  },
  {
    nameKey: "testimonials.user2_name",
    roleKey: "testimonials.user2_role",
    contentKey: "testimonials.user2_content",
    rating: 5,
    avatar: "SD",
  },
  {
    nameKey: "testimonials.user3_name",
    roleKey: "testimonials.user3_role",
    contentKey: "testimonials.user3_content",
    rating: 5,
    avatar: "TB",
  },
];

/* ─── Color palette for plan cards ──────────────────────────────────── */
const PLAN_COLORS = [
  { border: "border-slate-500/30", glow: "shadow-slate-500/10", badge: "bg-slate-500/10 text-slate-400", btn: "bg-white/5 hover:bg-white/10 border border-white/10" },
  { border: "border-indigo-500/50", glow: "shadow-indigo-500/20", badge: "bg-indigo-500/10 text-indigo-400", btn: "bg-indigo-500 hover:bg-indigo-400 text-white" },
  { border: "border-violet-500/40", glow: "shadow-violet-500/15", badge: "bg-violet-500/10 text-violet-400", btn: "bg-white/5 hover:bg-white/10 border border-white/10" },
  { border: "border-rose-500/30", glow: "shadow-rose-500/10", badge: "bg-rose-500/10 text-rose-400", btn: "bg-white/5 hover:bg-white/10 border border-white/10" },
];

/* ─── Counter animation hook ────────────────────────────────────────── */
function useCountUp(target, duration = 1500, start = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setValue(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return value;
}

/* ─── Main component ────────────────────────────────────────────────── */
export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { branding } = useBranding();

  const [plans, setPlans] = useState([]);
  const [globalStats, setGlobalStats] = useState(null);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef(null);

  /* Fetch data from backend */
  useEffect(() => {
    // Plans
    investmentService.getPublicPlans()
      .then((res) => {
        if (res?.success && Array.isArray(res.data)) {
          setPlans(res.data);
        } else {
          setPlans([]);
        }
      })
      .catch(() => setPlansError(t("home.plans_error")))
      .finally(() => setPlansLoading(false));

    // Global Stats
    import("../services/api").then(({ default: api }) => {
      api.get("/public/stats")
        .then(res => {
          if (res.success) setGlobalStats(res.data);
        })
        .catch(err => console.error("Error loading public stats:", err));
    });
  }, [t]);

  /* Intersection observer for stats counter animation */
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  /* Features list */
  const FEATURES = [
    { icon: TrendingUp, color: "emerald", titleKey: "home.feat_trading_title", descKey: "home.feat_trading_desc", to: "/spot" },
    { icon: Zap,        color: "indigo",  titleKey: "home.feat_invest_title",  descKey: "home.feat_invest_desc",  to: "/packs" },
    { icon: Users,      color: "violet",  titleKey: "home.feat_mlm_title",     descKey: "home.feat_mlm_desc",     to: "/mlm" },
    { icon: Shield,     color: "rose",    titleKey: "home.feat_security_title", descKey: "home.feat_security_desc", to: "/register" },
  ];

  return (
    <div className="min-h-screen bg-[#020617] text-white overflow-x-hidden" style={{ fontFamily: "'Inter', sans-serif" }}>
      <Header />

      {/* ─── HERO ──────────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center justify-center px-6 pt-24 pb-20">
        {/* Background glows */}
        <div className="absolute top-1/4 left-1/4 w-full max-w-[700px] h-[700px] bg-indigo-600/8 rounded-full blur-[180px] pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-full max-w-[600px] h-[600px] bg-violet-600/8 rounded-full blur-[180px] pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-[900px] h-[900px] bg-indigo-500/3 rounded-full blur-[200px] pointer-events-none" />

        {/* Grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)", backgroundSize: "60px 60px" }}
        />

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          {/* Live badge */}
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-500/10 border border-indigo-500/20 rounded-full text-indigo-300 text-xs font-bold uppercase tracking-[0.15em] mb-10 backdrop-blur-sm">
            <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
            {t("home.live_badge")} — 500K+ {t("home.active_traders")}
          </div>

          {/* Hero title */}
          <h1
            className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tight leading-[1.02] mb-8"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            <span className="block text-white">{branding?.hero_title?.split('\n')[0] || t("landing.hero_title")}</span>
            <span className="block bg-gradient-to-r from-indigo-400 via-violet-400 to-indigo-300 bg-clip-text text-transparent">
              {branding?.hero_title?.split('\n')[1] || t("landing.hero_title_highlight")}
            </span>
          </h1>

          {/* Hero subtitle */}
          <p className="text-slate-400 text-lg md:text-xl font-medium max-w-2xl mx-auto mb-14 leading-relaxed">
            {branding?.hero_subtitle || t("landing.hero_subtitle")}
          </p>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              id="hero-cta-register"
              onClick={() => navigate("/register")}
              className="group px-10 py-5 bg-indigo-500 hover:bg-indigo-400 rounded-2xl font-black text-sm uppercase tracking-[0.12em] transition-all duration-200 shadow-2xl shadow-indigo-500/30 hover:shadow-indigo-500/50 flex items-center justify-center gap-3 active:scale-95"
            >
              {t("home.hero_cta_start")}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              id="hero-cta-markets"
              onClick={() => navigate("/spot")}
              className="px-10 py-5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-2xl font-black text-sm uppercase tracking-[0.12em] transition-all duration-200 flex items-center justify-center gap-3 backdrop-blur-sm"
            >
              <BarChart2 size={18} />
              {t("home.hero_cta_markets")}
            </button>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-6 mt-14">
            {[
              { icon: Lock, label: "SSL Secured" },
              { icon: Award, label: "Licensed" },
              { icon: Activity, label: "99.9% Uptime" },
              { icon: Cpu, label: "AI Powered" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wider">
                <Icon size={13} className="text-indigo-500" />
                {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── STATS ──────────────────────────────────────────────────── */}
      <section ref={statsRef} className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { labelKey: "home.stats_users",    value: "500K+", icon: Users },
            { labelKey: "home.stats_volume",   value: "$1.5M+", icon: BarChart2 },
            { labelKey: "home.stats_countries",value: globalStats?.countries || "47",      icon: Globe },
            { labelKey: "home.stats_uptime",   value: globalStats?.uptime || "99.9%",   icon: Shield },
          ].map(({ labelKey, value, icon: Icon }) => (
            <div
              key={labelKey}
              className="group p-7 bg-white/[0.02] border border-white/5 rounded-2xl text-center hover:border-indigo-500/20 hover:bg-white/[0.04] transition-all duration-300"
            >
              <Icon size={22} className="text-indigo-400 mx-auto mb-4 group-hover:scale-110 transition-transform" />
              <div className="text-3xl font-black text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{value}</div>
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.15em]">{t(labelKey)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── FEATURES ───────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-indigo-500/10 border border-indigo-500/20 rounded-full text-indigo-400 text-xs font-bold uppercase tracking-widest mb-5">
            <Zap size={11} />
            Platform
          </div>
          <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {t("home.features_title")}{" "}
            <span className="bg-gradient-to-r from-indigo-400 to-violet-400 bg-clip-text text-transparent">
              {t("home.features_title_highlight")}
            </span>
          </h2>
          <p className="text-slate-400 font-medium max-w-xl mx-auto text-lg">
            {t("home.features_subtitle")}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {FEATURES.map(({ icon: Icon, color, titleKey, descKey, to }) => (
            <div
              key={titleKey}
              className="group p-8 bg-white/[0.02] border border-white/5 rounded-3xl hover:border-indigo-500/20 hover:bg-white/[0.04] transition-all duration-300 cursor-pointer relative overflow-hidden"
              onClick={() => navigate(to)}
            >
              {/* Hover glow */}
              <div className={`absolute inset-0 bg-${color}-500/0 group-hover:bg-${color}-500/3 transition-all duration-500 rounded-3xl`} />

              <div className={`relative w-14 h-14 rounded-2xl bg-${color}-500/10 border border-${color}-500/20 flex items-center justify-center text-${color}-400 mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <Icon size={26} />
              </div>
              <h3 className="relative text-xl font-black mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {t(titleKey)}
              </h3>
              <p className="relative text-slate-400 font-medium leading-relaxed text-sm">{t(descKey)}</p>
              <div className="relative flex items-center gap-2 mt-6 text-indigo-400 text-xs font-bold uppercase tracking-[0.12em] group-hover:gap-3 transition-all">
                {t("home.learn_more")} <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── INVESTMENT PLANS (from backend) ────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-indigo-500/10 border border-indigo-500/20 rounded-full text-indigo-400 text-xs font-bold uppercase tracking-widest mb-5">
            <TrendingUp size={11} />
            Investments
          </div>
          <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {t("home.plans_title")}{" "}
            <span className="bg-gradient-to-r from-indigo-400 to-violet-400 bg-clip-text text-transparent">
              {t("home.plans_title_highlight")}
            </span>
          </h2>
          <p className="text-slate-400 font-medium max-w-xl mx-auto text-lg">
            {t("home.plans_subtitle")}
          </p>
        </div>

        {/* Loading */}
        {plansLoading && (
          <div className="flex flex-col items-center gap-4 py-16 text-slate-500">
            <div className="w-10 h-10 border-2 border-indigo-500/30 border-t-indigo-400 rounded-full animate-spin" />
            <span className="text-sm font-medium">{t("home.plans_loading")}</span>
          </div>
        )}

        {/* Error */}
        {!plansLoading && plansError && (
          <div className="text-center py-16">
            <div className="inline-flex items-center gap-3 px-6 py-4 bg-rose-500/10 border border-rose-500/20 rounded-2xl text-rose-400 text-sm font-medium">
              <Shield size={16} />
              {plansError}
            </div>
          </div>
        )}

        {/* Empty */}
        {!plansLoading && !plansError && plans.length === 0 && (
          <div className="text-center py-16 text-slate-500 text-sm font-medium">
            {t("home.plans_empty")}
          </div>
        )}

        {/* Plans grid */}
        {!plansLoading && !plansError && plans.length > 0 && (
          <div className={`grid gap-6 ${plans.length === 1 ? 'max-w-sm mx-auto' : plans.length === 2 ? 'md:grid-cols-2 max-w-2xl mx-auto' : 'md:grid-cols-3'}`}>
            {plans.map((plan, idx) => {
              const colorSet = PLAN_COLORS[idx % PLAN_COLORS.length];
              const isPopular = idx === 1 && plans.length >= 2;
              const dailyReturn = plan.daily_return_percent
                ? `${parseFloat(plan.daily_return_percent).toFixed(1)}%`
                : "—";
              const minAmount = plan.min_amount
                ? `${parseFloat(plan.min_amount).toLocaleString()} USDT`
                : "—";
              const duration = plan.duration_days || "—";

              return (
                <div
                  key={plan.id || idx}
                  className={`relative p-8 border ${colorSet.border} rounded-3xl transition-all duration-300 hover:scale-[1.02] cursor-pointer shadow-2xl ${colorSet.glow} ${isPopular ? 'bg-indigo-500/10' : 'bg-white/[0.02] hover:bg-white/[0.04]'}`}
                  onClick={() => navigate("/packs")}
                >
                  {/* Popular badge */}
                  {isPopular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-5 py-1.5 bg-indigo-500 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg shadow-indigo-500/40">
                      <Star size={10} fill="white" />
                      {t("home.plan_popular")}
                    </div>
                  )}

                  {/* Plan name */}
                  <h3 className="text-xl font-black mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {plan.name}
                  </h3>

                  {/* Daily return */}
                  <div className={`inline-flex items-center gap-2 px-3 py-1 ${colorSet.badge} rounded-lg text-xs font-bold uppercase tracking-wider mb-2`}>
                    <Activity size={11} />
                    {t("home.plan_daily_return")}
                  </div>
                  <div className="text-4xl font-black text-indigo-400 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {dailyReturn}
                  </div>
                  <div className="text-xs text-slate-500 font-bold uppercase tracking-widest mb-8">/ {t("home.plan_days")}</div>

                  {/* Features */}
                  <div className="space-y-3 mb-8">
                    {[
                      t("home.plan_feature_dashboard"),
                      t("home.plan_feature_mlm"),
                      t("home.plan_feature_support"),
                    ].map((feature) => (
                      <div key={feature} className="flex items-center gap-3 text-sm font-medium text-slate-300">
                        <CheckCircle size={14} className="text-emerald-400 shrink-0" />
                        {feature}
                      </div>
                    ))}
                  </div>

                  {/* Meta */}
                  <div className="space-y-2 mb-8">
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500 font-semibold uppercase tracking-wider">{t("home.plan_min_deposit")}</span>
                      <span className="text-slate-300 font-bold">{minAmount}</span>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500 font-semibold uppercase tracking-wider">{t("home.plan_duration")}</span>
                      <span className="text-slate-300 font-bold">{duration} {t("home.plan_days")}</span>
                    </div>
                  </div>

                  <button
                    className={`w-full py-3.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all duration-200 ${colorSet.btn}`}
                  >
                    {t("home.plan_cta")}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ─── TESTIMONIALS ───────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-indigo-500/10 border border-indigo-500/20 rounded-full text-indigo-400 text-xs font-bold uppercase tracking-widest mb-5">
            <Users size={11} />
            Community
          </div>
          <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {t("home.testimonials_title")}{" "}
            <span className="bg-gradient-to-r from-indigo-400 to-violet-400 bg-clip-text text-transparent">
              {t("home.testimonials_title_highlight")}
            </span>
          </h2>
          <p className="text-slate-400 font-medium">{t("home.testimonials_subtitle")}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map(({ nameKey, roleKey, contentKey, rating, avatar }) => (
            <div
              key={nameKey}
              className="p-8 bg-white/[0.02] border border-white/5 rounded-3xl hover:border-indigo-500/20 hover:bg-white/[0.04] transition-all duration-300 group"
            >
              {/* Quote icon */}
              <Quote size={28} className="text-indigo-500/40 mb-5 group-hover:text-indigo-500/60 transition-colors" />

              {/* Stars */}
              <div className="flex gap-1 mb-5">
                {Array.from({ length: rating }).map((_, i) => (
                  <Star key={i} size={14} className="text-amber-400 fill-amber-400" />
                ))}
              </div>

              {/* Content */}
              <p className="text-slate-300 text-sm font-medium leading-relaxed mb-7">
                "{t(contentKey)}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-300 text-xs font-black">
                  {avatar}
                </div>
                <div>
                  <div className="text-sm font-bold text-white">{t(nameKey)}</div>
                  <div className="text-xs text-slate-500 font-medium">{t(roleKey)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-4 mt-10">
          {[
            { label: t("testimonials.active_users"), value: t("testimonials.active_users_val") },
            { label: t("testimonials.traded_volume"), value: t("testimonials.traded_volume_val") },
            { label: t("testimonials.client_satisfaction"), value: t("testimonials.client_satisfaction_val") },
          ].map(({ label, value }) => (
            <div key={label} className="p-5 bg-white/[0.02] border border-white/5 rounded-2xl text-center">
              <div className="text-2xl font-black text-indigo-400 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{value}</div>
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── CTA FINAL ──────────────────────────────────────────────── */}
      <section className="max-w-4xl mx-auto px-6 py-24 text-center">
        <div className="relative p-14 rounded-3xl overflow-hidden">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 via-violet-500/10 to-indigo-600/10 rounded-3xl" />
          <div className="absolute inset-0 border border-indigo-500/20 rounded-3xl" />
          {/* Glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-indigo-500/10 rounded-full blur-[100px]" />

          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-indigo-500/10 border border-indigo-500/20 rounded-full text-indigo-400 text-xs font-bold uppercase tracking-widest mb-8">
              <Clock size={11} />
              {t("home.live_badge")}
            </div>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t("home.cta_title")}
            </h2>
            <p className="text-slate-400 font-medium text-lg mb-12 max-w-lg mx-auto">
              {t("home.cta_subtitle")}
            </p>
            <button
              id="cta-register"
              onClick={() => navigate("/register")}
              className="group px-12 py-5 bg-indigo-500 hover:bg-indigo-400 rounded-2xl font-black text-sm uppercase tracking-[0.12em] transition-all duration-200 shadow-2xl shadow-indigo-500/40 hover:shadow-indigo-500/60 inline-flex items-center gap-3 active:scale-95"
            >
              {t("home.cta_button")}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>

            {/* Security notes */}
            <div className="flex flex-wrap justify-center gap-6 mt-10">
              {[
                { icon: Lock, label: "SSL 256-bit" },
                { icon: Shield, label: "KYC Verified" },
                { icon: Award, label: "Licensed" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2 text-slate-500 text-xs font-semibold">
                  <Icon size={13} className="text-emerald-500" />
                  {label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}